import {createContext, useContext, useEffect, useState} from 'react';
import {
    clearRememberLastUrl,
    clearUserSessionData,
    getRememberLastUrl,
    getUserSessionData,
    getUserSessionToken,
    isCurrUrlOfAuthRoute,
    printError,
    printLog,
    sendRequest
} from "../utils/AppUtils";
import {
    checkCurrUrlHasParam,
    checkNullJson,
    checkNullStr,
    createTimeout,
    getCurrUrlParamValue,
    getDefStr,
    getJsonValue,
    getJsonValueFromNestedKeys,
    isJsonValueTrue
} from "../../common/fusion-ui/utils/ReactUtils";
import * as jwt_decode from 'jwt-decode';
import {useLocation} from "react-router-dom";
import {GenRouteUrls, Keys, ModuleFieldsData, ModuleRouteUrls, Modules, ReqUrls} from "../config/AppConfig";
import {CssVariant} from "../../common/fusion-ui/config/FusionConfig";
import {useToast} from "../../common/fusion-ui/provider/ToastProvider";
import {useLocalStoreData} from "./LocalStoreProvider";
import {SESSION_RECHECK_INTERVAL_SECONDS} from "../../common/CommonConfig";

const TAG = 'AuthProvider';

/**
 * Context to handle user authentication.
 * @type {React.Context<{ isAuthenticated: boolean }>}
 */
const AuthContext = createContext();

/**
 * Provider component to handle user authentications.
 * @component
 * @param {object} props - React component props.
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider.
 */
function AuthProvider({children}) {
    const location = useLocation();
    const {showToast, changeToastVariant} = useToast();
    const {updateLocalStoreField,} = useLocalStoreData();

    const lastRememberUrl = getRememberLastUrl();

    const [isAuthenticated, setIsAuthenticated] = useState(checkIfRegularSessionActive());
    const [isEmptyDataVerifDone, setIsEmptyDataVerifDone] = useState(false);
    const [bypassTokenData, setBypassTokenData] = useState({
        urlHasBypassToken: checkIfUrlContainsBypassParams(),
        bypassTokenChecking: checkIfUrlContainsBypassParams(),
        bypassTokenVerified: false,
        bypassTokenCheckMsg: '',
        alreadyChecked: false,
        bypassToken: '',
    });

    useEffect(() => {
        // log('useEffect.bypassTokenData:', bypassTokenData);
        verifyBypassToken();

        if (!isEmptyDataVerifDone && (!bypassTokenData.urlHasBypassToken || (bypassTokenData.urlHasBypassToken && bypassTokenData.bypassTokenVerified))) {
            // if (!isEmptyDataVerifDone) {
            // log('useEffect.near.verifySession');
            // Function to run perpetually
            const verifySession = () => {
                // const fun = 'verifySession:';
                try {
                    const isSessionActive = checkIfRegularSessionActive();
                    // log(fun, 'isSessionActive:', isSessionActive);
                    if (isSessionActive) {
                        if (isCurrUrlOfAuthRoute(location)) {
                            window.location.href = getDefStr(lastRememberUrl, ModuleRouteUrls[Modules.Dashboard]);
                        }
                    } else {
                        //Perform actions when session isn't active
                        setIsEmptyDataVerifDone(true);
                        setIsAuthenticated(false);
                        clearUserSessionData();

                        if (!isCurrUrlOfAuthRoute(location)) {
                            window.location.href = GenRouteUrls.login;
                        }
                        //clear interval ID
                        try {
                            clearInterval(intervalId);
                        } catch (error) {
                            // logErr(fun,error);
                        }
                    }
                } catch (error) {
                    logErr(fun, error);
                }
            };

            // Run the function initially
            verifySession();

            // Set interval to run the function every 10 seconds
            const intervalId = setInterval(verifySession, SESSION_RECHECK_INTERVAL_SECONDS);

            // Cleanup function to clear interval when component unmounts
            return () => clearInterval(intervalId);
        }
    }, []);

    function checkIfRegularSessionActive() {
        const fun = 'checkIfRegularSessionActive:';
        const sessionDataFound = checkNullJson(getUserSessionData());
        let tokenExpired = false;

        //check if token is expired or not
        if (sessionDataFound && checkNullStr(getUserSessionToken())) {
            try {
                const decodedToken = jwt_decode.jwtDecode(getUserSessionToken());
                const tokenExpTimestamp = decodedToken.exp;
                const currentTime = Date.now() / 1000;
                tokenExpired = tokenExpTimestamp < currentTime; // Token has expired
                // log(fun, 'sessionDataFound.token.decodedToken:', decodedToken, '\ntokenExpired:', tokenExpired, '\ncurrentTime:', currentTime, '\ncurrent-time:', timestampToReadableTime(currentTime), ', tokenExpTimestamp-time:', timestampToReadableTime(tokenExpTimestamp));
            } catch (error) {
                logErr(fun, 'error:', error);
                tokenExpired = true;
            }
        } else {
            tokenExpired = true;
        }
        return sessionDataFound && !tokenExpired;
    }

    function checkIfUrlContainsBypassParams() {
        const fun = 'checkIfUrlContainsBypassParams:';
        let hasParams = false;
        try {
            hasParams = checkCurrUrlHasParam(Keys.urlParam_loginBypassTokenAlias);
            // const bypassToken = getCurrUrlParamValue(Keys.urlParam_loginBypassTokenAlias);
            // const bypassTokenFound = checkNullStr(bypassToken);
            // log(fun, 'hasParams:', hasParams, 'bypassTokenData:', bypassToken, 'bypassTokenFound:', bypassTokenFound);

            hasParams = hasParams && checkNullStr(getCurrUrlParamValue(Keys.urlParam_loginBypassTokenAlias));
        } catch (error) {
            logErr(fun, error);
        }
        return hasParams;
    }

    function verifyBypassToken() {
        const fun = 'verifyBypassToken:';
        // const tokenAlias = getCurrUrlParamValue(Keys.urlParam_loginBypassTokenAlias);
        // log(fun, 'Entered bypass-token-verify function.alreadyChecked:', bypassTokenData.alreadyChecked, 'tokenAlias:', bypassTokenData.bypassToken);
        if (!bypassTokenData.alreadyChecked && checkIfUrlContainsBypassParams()) {
            try {
                const tokenAlias = getCurrUrlParamValue(Keys.urlParam_loginBypassTokenAlias);
                // log(fun, 'Found bypass token-alias:', tokenAlias);

                // const proceed = false;
                const proceed = true;
                if (proceed) {
                    setBypassTokenData(prevState => ({
                        ...prevState,
                        urlHasBypassToken: true,
                        bypassTokenChecking: true,
                        bypassTokenVerified: false,
                        bypassTokenCheckMsg: 'Verifying link...',
                        alreadyChecked: true,
                    }));

                    //send verification request
                    const REQUEST_OPTIONS = {
                        tokenAlias: tokenAlias,
                    };
                    sendRequest({
                        reqUrl: ReqUrls.loginBypass_verifyToken,
                        reqOptions: REQUEST_OPTIONS,
                        onFetched: ((fetchedData) => {
                            log(fun, 'fetchedData:', fetchedData);//tokenRecord, userRecord, companyRecord, success, message

                            const tokenRecord = getJsonValueFromNestedKeys(fetchedData, ['data', 'tokenRecord']);
                            const userRecord = getJsonValueFromNestedKeys(fetchedData, ['data', 'userRecord']);
                            const companyRecord = getJsonValueFromNestedKeys(fetchedData, ['data', 'companyRecord']);
                            const success = isJsonValueTrue(fetchedData, 'success');
                            const message = getJsonValue(fetchedData, 'message');
                            setIsAuthenticated(success);
                            createTimeout(() => {
                                setBypassTokenData(prevState => ({
                                    ...prevState,
                                    bypassToken: success ? tokenAlias : '',
                                    bypassTokenChecking: false,
                                    bypassTokenVerified: success,
                                    bypassTokenCheckMsg: message,
                                }));
                                if (success) {
                                    toast(message, CssVariant.success);

                                    //Clear old-storage
                                    clearUserSessionData();

                                    //store user-data & token in local-storage
                                    updateLocalStoreField(Keys.sessionData, userRecord, true);
                                    updateLocalStoreField(Keys.subdomainData, companyRecord, true);

                                    //Redirect to the intended page
                                    const mf_token = ModuleFieldsData[Modules.LoginBypass];
                                    const redirectUrl = getJsonValue(tokenRecord, mf_token.redirectUrl.column, getDefStr(lastRememberUrl, ModuleRouteUrls[Modules.Home]));

                                    createTimeout(() => {
                                        toast('Redirecting you to intended page...', CssVariant.info);
                                        createTimeout(() => {
                                            //send request to increase the used-count in database
                                            updateSharedUrlUsedCountInDb(tokenAlias);

                                            clearRememberLastUrl();
                                            window.location.href = redirectUrl;
                                        }, isCurrUrlOfAuthRoute(location) ? 0 : 1000);
                                    }, isCurrUrlOfAuthRoute(location) ? 0 : 1500);
                                    // }
                                } else {
                                    performFailureTasks(null, message);
                                }
                            }, 1500);
                        }),
                        onError: ((error) => {
                            performFailureTasks(error);
                        }),
                    });
                }
            } catch (error) {
                performFailureTasks(error);
            }
        }

        function performFailureTasks(error, msg = "Login bypass failed, please try again!", isAuthed = false) {
            toast(msg, CssVariant.danger);
            setBypassTokenData(prevState => ({
                ...prevState,
                bypassTokenChecking: false,
                bypassTokenVerified: false,
                bypassTokenCheckMsg: msg,
            }));
            if (!isCurrUrlOfAuthRoute(location)) {
                clearUserSessionData(window.location.href);

                // createTimeout(() => {
                //     setBypassTokenData(prevState => ({
                //         ...prevState,
                //         bypassTokenCheckMsg: 'Redirecting you to login-page...',
                //     }));
                //     toast('Redirecting you to login-page...', CssVariant.info);
                //     createTimeout(() => {
                //         clearUserSessionData(window.location.href);
                //         window.location.href = GenRouteUrls.login;
                //     }, isAuthenticated ? 1500 : 1500);
                // }, isAuthenticated ? 2500 : 2500);
            }
            if (error) logErr(fun, error);
        }
    }

    function updateSharedUrlUsedCountInDb(tokenAlias) {
        const fun = 'updateSharedUrlUsedCountInDb:';
        try {
            const proceed = true;
            if (proceed) {
                const REQ_OPTIONS = {
                    tokenAlias,
                };

                sendRequest({
                    reqUrl: ReqUrls.loginBypass_updateTokenUsedCount,
                    reqOptions: REQ_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);
                        const success = isJsonValueTrue(fetchedData, 'success');
                        const message = getJsonValue(fetchedData, 'message');

                        if (success) {
                            // toast(message, CssVariant.success);
                        } else {
                            // toast(message, CssVariant.danger);
                        }
                    }),
                    onError: ((error) => {
                        logErr(fun, error);
                        // toast(`Couldn't update used-count`, CssVariant.danger);
                    }),
                });
            }
        } catch (error) {
            logErr(fun, error);
            // toast(`Couldn't update used-count`, CssVariant.danger);
        }
    }

    return <AuthContext.Provider value={{
        isAuthenticated,
        ...bypassTokenData
    }}>
        {children}
    </AuthContext.Provider>

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }
}

/**
 * Custom hook to access the auth context values.
 * @function
 * @returns {{ isAuthenticated: boolean }} context values.
 *
 * @example
 * import React from 'react';
 * import { useAuth } from './AuthProvider';
 *
 * const YourComponent = () => {
 *   const { isAuthenticated } = useAuth();
 *
 *   const handleClick = () => {
 *     console.log('This is an auth message!', 'primary');
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleClick}>Test Auth</button>
 *     </div>
 *   );
 * };
 *
 * export default YourComponent;
 */
function useAuth() {
    return useContext(AuthContext);
}


function log(...text) {
    printLog(TAG, ...text);
}

function logErr(...text) {
    printError(TAG, ...text);
}

export {AuthProvider, useAuth};
